import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import EmptyState from '@/components/feature/EmptyState';
import ImageLightbox from '@/pages/detail/components/ImageLightbox';
import ShareBar from '@/pages/detail/components/ShareBar';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { getArticleById, getHotGossips, getRelatedArticles, getStarById } from '@/utils/articles';
import { formatDateTime, formatViews } from '@/utils/format';

export default function DetailPage() {
  const { id = '' } = useParams();
  const article = getArticleById(id);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState<number | null>(null);

  useDocumentMeta(
    article ? `${article.title} - 91黑料详情` : '爆料详情 - 91黑料',
    article ? article.excerpt : undefined,
  );

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 320);
    return () => window.clearTimeout(timer);
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-content mx-auto px-4 md:px-6 py-8">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8 animate-pulse">
          <div className="lg:col-span-2">
            <div className="h-5 w-48 bg-background-200 rounded mb-6"></div>
            <div className="h-8 w-3/4 bg-background-200 rounded mb-4"></div>
            <div className="h-5 w-1/2 bg-background-200 rounded mb-6"></div>
            <div className="w-full aspect-[16/9] bg-background-200 rounded-lg mb-6"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-4 bg-background-200 rounded"></div>
              ))}
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="h-64 bg-background-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="max-w-content mx-auto px-4 md:px-6 py-10">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="爆料已删除或不存在"
          description="你访问的爆料可能已被删除，或链接有误。返回首页看看最新的娱乐黑料吧。"
          actionTo="/"
          actionLabel="返回首页"
        />
      </div>
    );
  }

  const images = [article.cover, ...article.gallery];
  const related = getRelatedArticles(article, 5);
  const hotList = getHotGossips(5).filter((a) => a.id !== article.id).slice(0, 4);
  const star = article.starId ? getStarById(article.starId) : undefined;
  const isNews = article.type === 'news';

  return (
    <div className="max-w-content mx-auto px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb
        items={[
          isNews
            ? { label: '娱乐资讯', to: '/news' }
            : { label: '娱乐黑料', to: '/category/feiwen' },
          { label: article.categoryName, to: isNews ? '/news' : `/category/${article.category}` },
          { label: '正文' },
        ]}
      />

      <div className="mt-5 md:mt-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <article className="lg:col-span-2 min-w-0">
          <div className="flex items-center gap-2 mb-4">
            <Link
              to={isNews ? '/news' : `/category/${article.category}`}
              className="px-2.5 py-1 rounded-md text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
            >
              {article.categoryName}
            </Link>
            {article.isHot && (
              <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-accent-100 text-accent-800 flex items-center gap-0.5">
                <i className="ri-fire-line"></i> 热门爆料
              </span>
            )}
          </div>

          <h1 className="text-2xl md:text-3xl font-black text-foreground-950 leading-snug">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 pb-5 border-b border-background-200 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {formatDateTime(article.publishTime)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-newspaper-line"></i>
              来源：{article.source}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line"></i>
              {formatViews(article.views)} 阅读
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-thumb-up-line"></i>
              {formatViews(article.likes)} 点赞
            </span>
          </div>

          <button
            type="button"
            onClick={() => setLightbox(0)}
            className="block w-full mt-5 rounded-lg overflow-hidden bg-background-100 group relative"
          >
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title}-91黑料详情`}
              className="w-full aspect-[16/9] object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
            />
            <span className="absolute bottom-3 right-3 px-2.5 py-1 rounded-md bg-foreground-950/70 text-background-50 text-xs flex items-center gap-1 backdrop-blur-sm">
              <i className="ri-zoom-in-line"></i> 点击放大
            </span>
          </button>

          <div className="mt-6 space-y-4 text-[15px] md:text-base text-foreground-800 leading-[1.9]">
            {article.content.slice(0, 2).map((p, i) => (
              <p key={`p1-${i}`} className="indent-8">
                {p}
              </p>
            ))}

            {article.gallery.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-6">
                {article.gallery.map((img, i) => (
                  <button
                    key={img}
                    type="button"
                    onClick={() => setLightbox(i + 1)}
                    className="block rounded-lg overflow-hidden bg-background-100 group relative"
                  >
                    <img
                      src={img}
                      alt={`${article.title} 相关图片 ${i + 1}`}
                      title={`${article.title}-相关素材`}
                      className="w-full aspect-[3/2] object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
                    />
                    <span className="absolute bottom-2 right-2 w-8 h-8 rounded-md bg-foreground-950/60 text-background-50 flex items-center justify-center backdrop-blur-sm">
                      <i className="ri-zoom-in-line"></i>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {article.content.slice(2).map((p, i) => (
              <p key={`p2-${i}`} className="indent-8">
                {p}
              </p>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-7 pt-5 border-t border-background-200">
            <i className="ri-price-tag-3-line text-foreground-400"></i>
            {article.tags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-700 text-xs hover:bg-background-200 transition-colors"
              >
                #{tag}
              </Link>
            ))}
          </div>

          {star && (
            <Link
              to={`/star/${star.id}`}
              className="flex items-center gap-4 mt-6 p-4 bg-background-50 border border-background-200 rounded-lg hover:border-primary-300 transition-colors"
            >
              <span className="w-14 h-14 rounded-full overflow-hidden bg-background-100 shrink-0">
                <img
                  src={star.avatar}
                  alt={star.name}
                  title={`${star.name}-91黑料明星专题`}
                  className="w-full h-full object-cover object-top"
                />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-foreground-500">相关明星专题</p>
                <p className="text-base font-bold text-foreground-950 mt-0.5">
                  {star.name} · {star.title}
                </p>
              </div>
              <span className="shrink-0 text-sm text-primary-600 flex items-center gap-1 whitespace-nowrap">
                查看专题 <i className="ri-arrow-right-s-line"></i>
              </span>
            </Link>
          )}

          <div className="mt-7 pt-5 border-t border-background-200">
            <ShareBar title={article.title} />
          </div>
        </article>

        <aside className="space-y-6">
          <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
            <div className="flex items-center gap-2 pb-3 mb-1 border-b border-background-200">
              <i className="ri-links-line text-primary-500 text-xl"></i>
              <h2 className="text-base font-black text-foreground-950">相关爆料推荐</h2>
            </div>
            {related.length === 0 ? (
              <p className="text-sm text-foreground-500 py-4">暂无相关推荐</p>
            ) : (
              <div className="space-y-1">
                {related.map((item) => (
                  <ArticleCard key={item.id} article={item} variant="compact" />
                ))}
              </div>
            )}
          </div>

          <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
            <div className="flex items-center gap-2 pb-3 mb-1 border-b border-background-200">
              <i className="ri-fire-line text-accent-600 text-xl"></i>
              <h2 className="text-base font-black text-foreground-950">热门黑料</h2>
            </div>
            <div>
              {hotList.map((item, i) => (
                <ArticleCard key={item.id} article={item} variant="compact" rank={i + 1} />
              ))}
            </div>
          </div>
        </aside>
      </div>

      {lightbox !== null && (
        <ImageLightbox
          images={images}
          index={lightbox}
          alt={article.title}
          onClose={() => setLightbox(null)}
          onChange={setLightbox}
        />
      )}
    </div>
  );
}